// Section schemas drive the dynamic admin editor.
// Field types: string, textarea, icon, url, bool, image, file, cta_kind, list, object, string_list

export const CTA_KIND_OPTIONS = [
    { value: "external", label: "External URL" },
    { value: "anchor", label: "In-page anchor" },
    { value: "whatsapp", label: "WhatsApp (service enquiry)" },
    { value: "resource_whatsapp", label: "WhatsApp (book/resource enquiry)" },
    { value: "email", label: "Email" },
];

const ctaKindField = { type: "cta_kind", label: "CTA behaviour" };
const visibleField = { type: "bool", label: "Visible on the public site" };

export const SECTION_SCHEMAS = {
    hero: {
        title: "Hero section",
        fields: {
            eyebrow: { type: "string", label: "Eyebrow line" },
            headline_line1: { type: "string", label: "Headline – line 1" },
            headline_line2: { type: "string", label: "Headline – line 2 (teal accent)" },
            subheadline: { type: "textarea", label: "Sub-headline" },
            intro: { type: "textarea", label: "Intro paragraph" },
            primary_cta_label: { type: "string", label: "Primary button label" },
            primary_cta_url: { type: "string", label: "Primary button URL (AWSAI)" },
            secondary_cta_label: { type: "string", label: "Secondary button label" },
            secondary_cta_kind: { type: "cta_kind", label: "Secondary button behaviour" },
            tertiary_cta_label: { type: "string", label: "Tertiary button label" },
            tertiary_cta_url: { type: "string", label: "Tertiary button URL" },
            quaternary_cta_label: { type: "string", label: "Quaternary button label" },
            quaternary_cta_kind: { type: "cta_kind", label: "Quaternary button behaviour" },
            background_image_url: { type: "image", label: "Background image" },
            audience_strip: { type: "string_list", label: "Audience tags (bottom strip)" },
        },
    },
    featured_programme: {
        title: "Featured Programme (Research Workshops)",
        fields: {
            eyebrow: { type: "string", label: "Eyebrow" },
            title: { type: "string", label: "Section title" },
            copy: { type: "textarea", label: "Section copy" },
            highlights: { type: "string_list", label: "Highlights (bullet list)" },
            cta_label: { type: "string", label: "Button label" },
            workshop_url: { type: "string", label: "Workshop landing page URL (base — utm params are added automatically)" },
            cohort_label: { type: "string", label: "Cohort card label" },
            cohort_title: { type: "string", label: "Cohort card title (e.g. Cohort 1, August 2026)" },
            cohort_lines: { type: "string_list", label: "Cohort card lines (schedule / time / timezone)" },
            schedule_heading: { type: "string", label: "Schedule heading" },
            schedule: {
                type: "list",
                label: "Workshop dates",
                item_label_field: "title",
                allow_add_remove: true,
                item_schema: {
                    date: { type: "string", label: "Date label (e.g. 1 August)" },
                    title: { type: "string", label: "Workshop title" },
                },
            },
            visible: { type: "bool", label: "Show this section on the public site" },
        },
    },
    services: {
        title: "Services section",
        fields: {
            eyebrow: { type: "string", label: "Eyebrow" },
            title: { type: "string", label: "Title" },
            subtitle: { type: "textarea", label: "Subtitle" },
            items: {
                type: "list",
                label: "Service cards",
                item_label_field: "title",
                allow_add_remove: true,
                item_schema: {
                    key: { type: "string", label: "Slug (no spaces; used for analytics)" },
                    icon: { type: "icon", label: "Icon" },
                    title: { type: "string", label: "Title" },
                    short: { type: "textarea", label: "Short description" },
                    long: { type: "textarea", label: "Long description" },
                    suitable: { type: "textarea", label: "Suitable for (optional)" },
                    cta_label: { type: "string", label: "Button label" },
                    cta_kind: ctaKindField,
                    cta_url: { type: "string", label: "Button URL (used when behaviour = External / In-page anchor)" },
                    visible: visibleField,
                },
            },
        },
    },
    awsai: {
        title: "AWSAI section",
        fields: {
            eyebrow: { type: "string" },
            title: { type: "string" },
            subtitle: { type: "string" },
            intro_paragraph_1: { type: "textarea", label: "Paragraph 1" },
            intro_paragraph_2: { type: "textarea", label: "Paragraph 2" },
            primary_cta_label: { type: "string", label: "Start Assessment label" },
            primary_cta_url: { type: "string", label: "Start Assessment URL" },
            secondary_cta_label: { type: "string", label: "Request Report label" },
            secondary_cta_url: { type: "string", label: "Request Report URL" },
            note: { type: "textarea", label: "Footer note (RM499 etc.)" },
            footer_text: { type: "textarea", label: "Right panel footer text" },
            dimensions: {
                type: "list",
                label: "Readiness dimensions",
                item_label_field: "label",
                allow_add_remove: true,
                item_schema: {
                    icon: { type: "icon" },
                    label: { type: "string" },
                },
            },
        },
    },
    smart_work: {
        title: "SMART-WORK section",
        fields: {
            eyebrow: { type: "string" },
            title: { type: "string" },
            subtitle: { type: "textarea" },
            availability_label: { type: "string", label: "Availability label (e.g. 'Available upon consultation')" },
            cta_label: { type: "string", label: "Consultation button label" },
            cta_kind: ctaKindField,
            cta_url: { type: "string", label: "Button URL (if External)" },
            stages: {
                type: "list",
                label: "Stages",
                item_label_field: "title",
                allow_add_remove: true,
                item_schema: {
                    n: { type: "string", label: "Number (e.g. 01)" },
                    icon: { type: "icon" },
                    title: { type: "string" },
                    desc: { type: "textarea", label: "Description" },
                },
            },
        },
    },
    research_training: {
        title: "Research training section",
        fields: {
            eyebrow: { type: "string" },
            title: { type: "string" },
            subtitle: { type: "textarea" },
            items: {
                type: "list",
                label: "Workshop cards",
                item_label_field: "title",
                allow_add_remove: true,
                item_schema: {
                    key: { type: "string", label: "Slug" },
                    icon: { type: "icon" },
                    title: { type: "string" },
                    desc: { type: "textarea", label: "Description" },
                    cta_label: { type: "string" },
                    cta_kind: ctaKindField,
                    cta_url: { type: "string", label: "Button URL (if External)" },
                    visible: visibleField,
                },
            },
        },
    },
    books: {
        title: "Books & resources section",
        fields: {
            eyebrow: { type: "string" },
            title: { type: "string" },
            subtitle: { type: "textarea" },
            intro: { type: "textarea" },
            items: {
                type: "list",
                label: "Resources",
                item_label_field: "title",
                allow_add_remove: true,
                item_schema: {
                    key: { type: "string", label: "Slug" },
                    icon: { type: "icon" },
                    tag: { type: "string", label: "Category tag" },
                    title: { type: "string" },
                    desc: { type: "textarea", label: "Description" },
                    suitable: { type: "textarea", label: "Suitable for" },
                    cover_url: { type: "image", label: "Book cover (upload or paste URL)" },
                    file_url: { type: "file", label: "Sample PDF (upload or paste URL)" },
                    cta_label: { type: "string" },
                    cta_kind: ctaKindField,
                    cta_url: { type: "string", label: "Button URL (if External)" },
                    visible: visibleField,
                },
            },
        },
    },
    why_choose: {
        title: "Why Choose Aurea section",
        fields: {
            eyebrow: { type: "string" },
            title: { type: "string" },
            items: {
                type: "list",
                label: "Reasons",
                item_label_field: "title",
                allow_add_remove: true,
                item_schema: {
                    icon: { type: "icon" },
                    title: { type: "string" },
                    desc: { type: "textarea" },
                },
            },
        },
    },
    about: {
        title: "About section",
        fields: {
            eyebrow: { type: "string" },
            title: { type: "string" },
            paragraph_1: { type: "textarea" },
            paragraph_2: { type: "textarea" },
            background_note: { type: "textarea", label: "Background note (smaller, italic at bottom)" },
        },
    },
    contact: {
        title: "Contact & enquiry templates",
        fields: {
            eyebrow: { type: "string" },
            title: { type: "string" },
            intro: { type: "textarea" },
            email: { type: "string", label: "Email address" },
            whatsapp_number: { type: "string", label: "WhatsApp number (digits only, e.g. 60102306534)" },
            whatsapp_display: { type: "string", label: "WhatsApp display format (e.g. +60 10-230 6534)" },
            whatsapp_template: { type: "textarea", label: "WhatsApp message template (use {area} placeholder)" },
            resource_whatsapp_template: { type: "textarea", label: "Resource enquiry template (use {resource})" },
            email_subject: { type: "string", label: "Email subject" },
            email_template: { type: "textarea", label: "Email body template (use {area})" },
        },
    },
    footer: {
        title: "Footer",
        fields: {
            tagline: { type: "textarea", label: "Tagline" },
            copyright_suffix: { type: "string", label: "Copyright suffix (year is auto-added)" },
            credit_line: { type: "string", label: "Footer credit line" },
            groups: {
                type: "list",
                label: "Link groups",
                item_label_field: "heading",
                allow_add_remove: true,
                item_schema: {
                    heading: { type: "string" },
                    links: {
                        type: "list",
                        label: "Links",
                        item_label_field: "label",
                        allow_add_remove: true,
                        item_schema: {
                            label: { type: "string" },
                            href: { type: "string", label: "URL (use # for in-page anchors)" },
                            external: { type: "bool", label: "Open in new tab" },
                        },
                    },
                },
            },
        },
    },
    seo: {
        title: "SEO & social",
        fields: {
            title: { type: "string", label: "Page title" },
            description: { type: "textarea", label: "Meta description" },
            og_title: { type: "string", label: "Open Graph title" },
            og_description: { type: "textarea", label: "Open Graph description" },
            og_image_url: { type: "image", label: "Open Graph image" },
        },
    },
};

export const SECTION_ORDER = [
    "hero",
    "featured_programme",
    "services",
    "awsai",
    "smart_work",
    "research_training",
    "books",
    "why_choose",
    "about",
    "contact",
    "footer",
    "seo",
];
